import { Sparkles } from 'lucide-react';
import { Contract } from '../../types';

/**
 * The AI capture-analyst verdict, rendered as the polished card users see on the
 * AI Best Matches page. When the AI has scored a (user, contract), its fit_score
 * SUPERSEDES the deterministic match score, so this card is the authoritative
 * read: tier + eligibility + rationale + opportunities/blockers + recommended
 * action. Single source of truth — reused by the contract detail view and the
 * solicitation slideout so the "beautiful UI" stays consistent and accurate.
 *
 * Renders nothing unless the contract is AI-scored.
 */
export function AIVerdictCard({
  contract,
  compact = false,
}: {
  contract: Contract;
  compact?: boolean;
}) {
  if (!contract.aiScored) return null;

  const maxItems = compact ? 2 : 3;

  return (
    <div className="rounded-xl border border-bw-primary/30 bg-bw-primary-tint p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-bw-primary font-semibold text-sm">
          <Sparkles className="h-4 w-4" /> AI Fit Analysis
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-lg text-bw-ink">
            {Math.round(contract.matchScore || 0)}
          </span>
          {contract.aiTier && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-bw-surface text-bw-ink border border-bw-border">
              {contract.aiTier}
            </span>
          )}
        </div>
      </div>

      {contract.aiEligibility && (
        <div
          className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold mb-2 ${
            contract.aiEligibility === 'eligible'
              ? 'bg-success-bg text-success'
              : contract.aiEligibility === 'ineligible'
                ? 'bg-urgent-bg text-urgent'
                : 'bg-fit-moderate-bg text-fit-moderate'
          }`}
        >
          {contract.aiEligibility === 'eligible'
            ? 'Eligible'
            : contract.aiEligibility === 'ineligible'
              ? 'Ineligible'
              : 'Conditionally eligible'}
        </div>
      )}

      {contract.aiRationale && (
        <p className="text-sm text-bw-ink mb-2">{contract.aiRationale}</p>
      )}

      {contract.aiOpportunities && contract.aiOpportunities.length > 0 && (
        <ul className="text-xs text-bw-ink/80 space-y-0.5 mb-2">
          {contract.aiOpportunities.slice(0, maxItems).map((o, i) => (
            <li key={i}>✓ {o}</li>
          ))}
        </ul>
      )}

      {contract.aiBlockers && contract.aiBlockers.length > 0 && (
        <ul className="text-xs text-urgent space-y-0.5 mb-2">
          {contract.aiBlockers.slice(0, maxItems).map((b, i) => (
            <li key={i}>⚠ {b}</li>
          ))}
        </ul>
      )}

      {contract.aiRecommendedAction && (
        <p className="text-xs font-semibold text-bw-ink">
          Recommended: {contract.aiRecommendedAction}
        </p>
      )}
    </div>
  );
}
